import { WidgetId } from '../../types/idTypes';

/**
 * A singleton in memory { widgetid - [line widget id] } cache
 * Used for checking if a widget is connected to any lines
 *
 * Format:
 * {
 *  [widgetId]: WidgetId[]
 * }
 * - widgetId is the id of the widget
 * - WidgetId[] is the array of line widget ids that `widgetId` is attached to
 */
class LineConnectionCache {
  private idMap: Map<WidgetId, WidgetId[]>;
  private static instance: LineConnectionCache;

  constructor() {
    this.idMap = new Map();
  }

  isConnected = (widgetId: WidgetId): boolean => {
    return this.idMap.has(widgetId);
  };

  /**
   * Get all line widget ids that are attached to `widgetId`
   *
   * @param widgetId widget to check
   * @returns WidgetId[] array of widgets that `widgetId` is attached to - empty array if no connections
   */
  getAttachedLines = (widgetId: WidgetId): WidgetId[] => {
    return this.idMap.get(widgetId) || [];
  };

  /**
   * Remove connection between widget and line
   *
   * @param widgetId Widget id to detach line from
   * @param detachedLineId Line widget being detached
   * @returns
   */
  detachLineFromWidget = (widgetId: WidgetId, detachedLineId: WidgetId): void => {
    // Widget doesn't have connection
    const connectedLineIds = this.idMap.get(widgetId);
    if (!connectedLineIds) return;

    // Wasn't attached, don't need to update

    const detachedLineIdx = connectedLineIds.indexOf(detachedLineId);
    if (detachedLineIdx <= -1) return;

    const newConnectedLineIds = [...connectedLineIds];
    newConnectedLineIds.splice(detachedLineIdx, 1);

    // Remove from cache if no more lines connected to this widget
    if (!newConnectedLineIds.length) {
      this.idMap.delete(widgetId);
      return;
    }

    this.idMap.set(widgetId, newConnectedLineIds);
  };

  /**
   * Add connection between widget and line
   *
   * @param widgetId Widget id to to connect to
   * @param detachedLineId Line widget being connected
   * @returns
   */
  attachLineToWidget = (widgetId: WidgetId, attachedLineId: WidgetId): void => {
    const connectedLineIds = this.idMap.get(widgetId) || [];

    // Already attached, don't need to add again
    if (connectedLineIds.indexOf(attachedLineId) > -1) return;

    // Add new line
    connectedLineIds.push(attachedLineId);
    this.idMap.set(widgetId, connectedLineIds);
  };

  /**
   * Remove widget and its connections from cache
   *
   * @param widgetId Widget id to remove from cache
   */
  remove = (widgetId: WidgetId): void => {
    this.idMap.delete(widgetId);
  };

  clearCache = (): void => {
    this.idMap.clear();
  };

  public static getInstance(): LineConnectionCache {
    if (!LineConnectionCache.instance) {
      LineConnectionCache.instance = new LineConnectionCache();
    }

    return LineConnectionCache.instance;
  }
}

export const lineConnectionCache = LineConnectionCache.getInstance();
